import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminEmptyState,
  AdminErrorBanner,
  AdminFormField,
  AdminInfoRow,
  AdminPageLayout,
  AdminSelect,
  AdminStatusBadge,
  AdminTextarea,
  AdminTextInput,
} from "@/components/admin";
import { fetchAdminTermsPolicies, fetchAdminTermsPolicy, fetchAdminTermsPublishPreview, fetchDashboardSummary, publishAdminTermsPolicy, updateAdminTermsPolicy, type AdminTermsPublishPreview } from "@/lib/api";
import { formatDateTime, formatNullable } from "@/lib/format";
import type { AdminTermsPolicyItem, AdminTermsPolicySaveRequest, AdminTermsPolicyScope } from "@/types";
import { TermsMarkdown } from "@/components/terms/TermsMarkdown";
import { currentPublishedPolicyIds, versionedPublicPath } from "../terms/termsHistory";

const categoryTone: Record<string, "blue" | "teal" | "violet" | "amber" | "slate"> = {
  SERVICE: "blue",
  PRIVACY: "teal",
  LOCATION: "violet",
  SENSITIVE_INFO: "amber",
  THIRD_PARTY: "slate",
};

const requiredLabel = (requiredYn: string) => (requiredYn === "Y" ? "필수" : "선택");
const activeLabel = (useYn: string) => (useYn === "Y" ? "사용" : "미사용");
const publishLabel = (status: string) => status === "PUBLISHED" ? "게시" : status === "DRAFT" ? "초안" : status;

const toForm = (policy: AdminTermsPolicyItem): AdminTermsPolicySaveRequest => ({
  termsCategoryCode: policy.termsCategoryCode,
  termsTypeCode: policy.termsTypeCode,
  title: policy.title,
  content: policy.content,
  version: policy.version,
  requiredYn: policy.requiredYn,
  effectiveDate: policy.effectiveDate,
  displayOrder: policy.displayOrder,
  useYn: policy.useYn,
});

const TermsDetailPage = () => {
  const navigate = useNavigate();
  const { termsPolicyId } = useParams();
  const numericId = Number(termsPolicyId);
  const [items, setItems] = useState<AdminTermsPolicyItem[]>([]);
  const [policy, setPolicy] = useState<AdminTermsPolicyItem | null>(null);
  const [form, setForm] = useState<AdminTermsPolicySaveRequest | null>(null);
  const [scopes, setScopes] = useState<AdminTermsPolicyScope[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [publishPreview, setPublishPreview] = useState<AdminTermsPublishPreview | null>(null);
  const [publishReason, setPublishReason] = useState("");
  const [publishConfirmation, setPublishConfirmation] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [businessDate, setBusinessDate] = useState("");

  const loadPolicy = useCallback(async () => {
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setPolicy(null);
      setErrorMessage("약관 ID가 올바르지 않습니다.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    try {
      const [nextItems, detail, dashboard] = await Promise.all([
        fetchAdminTermsPolicies(),
        fetchAdminTermsPolicy(numericId),
        fetchDashboardSummary(),
      ]);
      const nextPolicy = detail.policy;
      setItems(nextItems);
      setScopes(detail.scopes);
      setPolicy(nextPolicy);
      setForm(nextPolicy ? toForm(nextPolicy) : null);
      setBusinessDate(dashboard.businessDate);
    } catch (error) {
      setPolicy(null);
      setForm(null);
      setErrorMessage(error instanceof Error ? error.message : "약관 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [numericId]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  const isCurrentPublished = useMemo(
    () => Boolean(policy && currentPublishedPolicyIds(items, businessDate).has(policy.termsPolicyId)),
    [businessDate, items, policy],
  );

  const categories = useMemo(() => {
    const uniqueCategories = new Map<string, string>();
    items.forEach((item) => uniqueCategories.set(item.termsCategoryCode, item.termsCategoryName));
    if (form?.termsCategoryCode && !uniqueCategories.has(form.termsCategoryCode)) {
      uniqueCategories.set(form.termsCategoryCode, form.termsCategoryCode);
    }
    return Array.from(uniqueCategories.entries()).map(([key, label]) => ({ key, label }));
  }, [form?.termsCategoryCode, items]);

  const types = useMemo(() => {
    const uniqueTypes = new Map<string, string>();
    items
      .filter((item) => !form?.termsCategoryCode || item.termsCategoryCode === form.termsCategoryCode)
      .forEach((item) => uniqueTypes.set(item.termsTypeCode, item.termsTypeName));
    if (form?.termsTypeCode && !uniqueTypes.has(form.termsTypeCode)) {
      uniqueTypes.set(form.termsTypeCode, form.termsTypeCode);
    }
    return Array.from(uniqueTypes.entries()).map(([key, label]) => ({ key, label }));
  }, [form?.termsCategoryCode, form?.termsTypeCode, items]);

  const updateForm = <K extends keyof AdminTermsPolicySaveRequest>(key: K, value: AdminTermsPolicySaveRequest[K]) => {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const handleCancelEdit = () => {
    setForm(policy ? toForm(policy) : null);
    setEditMode(false);
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (!policy || !form) return;
    if (!form.title.trim() || !form.content.trim() || !form.version.trim() || !form.termsCategoryCode || !form.termsTypeCode) {
      setErrorMessage("약관 분류, 유형, 제목, 버전, 본문을 입력해 주세요.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const saved = await updateAdminTermsPolicy(policy.termsPolicyId, {
        ...form,
        title: form.title.trim(),
        content: form.content.trim(),
        version: form.version.trim(),
        termsCategoryCode: form.termsCategoryCode.trim(),
        termsTypeCode: form.termsTypeCode.trim(),
        displayOrder: Number(form.displayOrder),
      });
      setPolicy(saved);
      setForm(toForm(saved));
      setEditMode(false);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "약관 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const loadPublishPreview = async () => {
    if (!policy) return;
    setErrorMessage("");
    try {
      setPublishPreview(await fetchAdminTermsPublishPreview(policy.termsPolicyId));
      setPublishConfirmation("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "게시 영향을 확인하지 못했습니다.");
    }
  };

  const handlePublish = async () => {
    if (!policy || !publishPreview?.executable || publishReason.trim().length < 10 || publishConfirmation !== publishPreview.confirmationText) return;
    setPublishing(true);
    setErrorMessage("");
    try {
      await publishAdminTermsPolicy(policy.termsPolicyId, {
        reasonCode: "LEGAL_APPROVAL",
        reason: publishReason.trim(),
        confirmationText: publishConfirmation,
      });
      setPublishPreview(null);
      setPublishReason("");
      setPublishConfirmation("");
      await loadPolicy();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "약관 게시에 실패했습니다.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <AdminPageLayout
      title="약관 상세"
      subtitle={policy ? `${policy.title} · ${policy.version}` : "약관 본문과 게시 상태를 확인합니다."}
      actions={
        <>
          <AdminButton variant="soft" size="sm" icon="ri-arrow-left-line" onClick={() => navigate("/admin/terms")}>
            목록
          </AdminButton>
          <AdminButton variant="soft" size="sm" icon="ri-refresh-line" onClick={() => void loadPolicy()} disabled={loading}>
            새로고침
          </AdminButton>
          {policy?.publishStatusCode === "PUBLISHED" && <>
            {isCurrentPublished && <a href={policy.publicPath} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-lg border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700">최신 공개본</a>}
            <a href={versionedPublicPath(policy)} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">이 버전 공개본</a>
          </>}
          {policy && policy.publishStatusCode === "DRAFT" && !editMode && (
            <AdminButton variant="primary" size="sm" icon="ri-edit-line" onClick={() => setEditMode(true)}>
              수정
            </AdminButton>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {errorMessage && <AdminErrorBanner message={errorMessage} />}

        {!policy && !loading ? (
          <AdminCard padded>
            <AdminEmptyState icon="ri-file-search-line" title="약관이 없습니다" description="목록에서 다시 약관을 선택해 주세요." />
          </AdminCard>
        ) : policy && form ? (
          <>
            <AdminCard className="overflow-hidden">
              <AdminCardHeader
                title={editMode ? "약관 수정" : "약관 정보"}
                icon={editMode ? "ri-edit-line" : "ri-file-text-line"}
                action={
                  editMode ? (
                    <div className="flex items-center gap-2">
                      <AdminButton variant="secondary" size="sm" onClick={handleCancelEdit} disabled={saving}>
                        취소
                      </AdminButton>
                      <AdminButton variant="primary" size="sm" icon="ri-save-line" onClick={() => void handleSave()} disabled={saving}>
                        {saving ? "저장 중" : "저장"}
                      </AdminButton>
                    </div>
                  ) : null
                }
              />

              {!editMode ? (
                <div className="p-5 space-y-5">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <AdminStatusBadge label={policy.termsCategoryName} tone={categoryTone[policy.termsCategoryCode] ?? "slate"} />
                      <AdminStatusBadge label={requiredLabel(policy.requiredYn)} tone={policy.requiredYn === "Y" ? "orange" : "slate"} />
                      <AdminStatusBadge label={activeLabel(policy.useYn)} tone={policy.useYn === "Y" ? "emerald" : "slate"} />
                      <AdminStatusBadge label={publishLabel(policy.publishStatusCode)} tone={policy.publishStatusCode === "PUBLISHED" ? "emerald" : "amber"} />
                    </div>
                    <h3 className="mt-3 text-xl font-bold text-slate-900">{policy.title}</h3>
                    <p className="mt-1 text-xs text-slate-400">
                      {policy.termsTypeName} · {policy.version}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <AdminInfoRow label="시행일" value={formatNullable(policy.effectiveDate)} />
                    <AdminInfoRow label="표시 순서" value={policy.displayOrder} />
                    <AdminInfoRow label="생성일" value={formatDateTime(policy.createdAt)} />
                    <AdminInfoRow label="수정일" value={formatDateTime(policy.updatedAt)} />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500">적용 대상 및 단계</h4>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {scopes.length === 0 ? <span className="text-xs text-amber-600">적용 scope가 없습니다.</span> : scopes.map((scope) => (
                        <span key={scope.termsPolicyScopeId} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                          {scope.targetUserTypeCode === "GUARDIAN" ? "Keeper" : scope.targetUserTypeCode === "SENIOR" ? "Senior" : "공통"} · {scope.agreementStageCode} · {scope.featureScopeCode}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <AdminFormField label="분류" htmlFor="terms-detail-category" required>
                      <AdminSelect
                        id="terms-detail-category"
                        value={form.termsCategoryCode}
                        onValueChange={(nextCategory) => {
                          const nextType = items.find((item) => item.termsCategoryCode === nextCategory)?.termsTypeCode ?? form.termsTypeCode;
                          setForm((current) => current ? { ...current, termsCategoryCode: nextCategory, termsTypeCode: nextType } : current);
                        }}
                      >
                        <option value="">분류 선택</option>
                        {categories.map((category) => (
                          <option key={category.key} value={category.key}>
                            {category.label}
                          </option>
                        ))}
                      </AdminSelect>
                    </AdminFormField>

                    <AdminFormField label="유형" htmlFor="terms-detail-type" required>
                      <AdminSelect
                        id="terms-detail-type"
                        value={form.termsTypeCode}
                        onValueChange={(nextValue) => updateForm("termsTypeCode", nextValue)}
                      >
                        <option value="">유형 선택</option>
                        {types.map((type) => (
                          <option key={type.key} value={type.key}>
                            {type.label}
                          </option>
                        ))}
                      </AdminSelect>
                    </AdminFormField>

                    <AdminFormField label="제목" htmlFor="terms-detail-title" required className="md:col-span-2">
                      <AdminTextInput
                        id="terms-detail-title"
                        value={form.title}
                        onChange={(event) => updateForm("title", event.target.value)}
                        maxLength={150}
                      />
                    </AdminFormField>

                    <AdminFormField label="버전" htmlFor="terms-detail-version" required>
                      <AdminTextInput
                        id="terms-detail-version"
                        value={form.version}
                        onChange={(event) => updateForm("version", event.target.value)}
                        maxLength={30}
                      />
                    </AdminFormField>

                    <AdminFormField label="시행일" htmlFor="terms-detail-effective-date" required>
                      <AdminTextInput
                        id="terms-detail-effective-date"
                        type="date"
                        value={form.effectiveDate}
                        onChange={(event) => updateForm("effectiveDate", event.target.value)}
                      />
                    </AdminFormField>

                    <AdminFormField label="필수 여부" htmlFor="terms-detail-required" required>
                      <AdminSelect
                        id="terms-detail-required"
                        value={form.requiredYn}
                        onValueChange={(nextValue) => updateForm("requiredYn", nextValue)}
                      >
                        <option value="Y">필수</option>
                        <option value="N">선택</option>
                      </AdminSelect>
                    </AdminFormField>

                    <AdminFormField label="상태" htmlFor="terms-detail-status" required>
                      <AdminSelect
                        id="terms-detail-status"
                        value={form.useYn}
                        onValueChange={(nextValue) => updateForm("useYn", nextValue)}
                      >
                        <option value="Y">사용</option>
                        <option value="N">미사용</option>
                      </AdminSelect>
                    </AdminFormField>

                    <AdminFormField label="표시 순서" htmlFor="terms-detail-display-order" required>
                      <AdminTextInput
                        id="terms-detail-display-order"
                        type="number"
                        value={form.displayOrder}
                        onChange={(event) => updateForm("displayOrder", Number(event.target.value))}
                        min={0}
                      />
                    </AdminFormField>
                  </div>
                </div>
              )}
            </AdminCard>

            <AdminCard className="overflow-hidden">
              <AdminCardHeader title="약관 본문" icon="ri-file-list-3-line" />
              {editMode ? (
                <div className="p-5">
                  <AdminTextarea
                    value={form.content}
                    onChange={(event) => updateForm("content", event.target.value)}
                    rows={24}
                    className="p-4 text-sm leading-7"
                  />
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs font-bold text-slate-500">사용자 화면 미리보기</p>
                    <TermsMarkdown content={form.content} />
                  </div>
                </div>
              ) : (
                <TermsMarkdown content={policy.content} className="p-6" />
              )}
            </AdminCard>

            {policy.publishStatusCode === "DRAFT" && !editMode && (
              <AdminCard padded>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div><h3 className="text-sm font-bold text-slate-900">약관 게시</h3><p className="mt-1 text-xs text-slate-500">게시 후 본문은 직접 수정할 수 없습니다. 법무 승인과 시행일을 확인하세요.</p></div>
                  <AdminButton variant="soft" size="sm" onClick={() => void loadPublishPreview()}>게시 영향 확인</AdminButton>
                </div>
                {publishPreview && <div className="mt-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
                  <p className="font-semibold">현재 상태 {publishPreview.currentStatus} · 활성 scope {publishPreview.activeScopeCount}건 · 게시 {publishPreview.executable ? "가능" : "차단"}</p>
                  {publishPreview.executable && <>
                    <AdminTextarea tone="warning" value={publishReason} onChange={(event) => setPublishReason(event.target.value)} rows={3} maxLength={500} placeholder="법무 승인 근거와 게시 사유를 10자 이상 입력하세요." />
                    <AdminTextInput tone="danger" value={publishConfirmation} onChange={(event) => setPublishConfirmation(event.target.value)} placeholder={`확인 문구: ${publishPreview.confirmationText}`} />
                    <AdminButton variant="danger" size="sm" disabled={publishing || publishReason.trim().length < 10 || publishConfirmation !== publishPreview.confirmationText} onClick={() => void handlePublish()}>{publishing ? "게시 중" : "약관 게시"}</AdminButton>
                  </>}
                </div>}
              </AdminCard>
            )}
          </>
        ) : null}
      </div>
    </AdminPageLayout>
  );
};

export default TermsDetailPage;
